import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Event as RouterEvent, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { REQUEST } from '@shared/common/injection-tokens/express.tokens';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { externalLinks } from '../../../constants/external-links.constants';
import { languageDetails, languages } from '../../../constants/language.constants';
import { AuthService } from '../../../core/services/auth/auth.service';
import { LanguageService } from '../../../core/services/language/language.service';
import { MessageService } from '../../../core/services/messages/messages.service';
import { ModalService } from '../../../core/services/modal/modal.service';
import { ResizeService } from '../../../core/services/resize/resize.service';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';
import { authType } from '../../../constants/auth-type';
import { Customer, CustomerProfile } from '../../../core/interfaces/customer';
import { Store } from '@ngxs/store';
import { LocationService } from '../../../core/services/location/location.service';
import { modalVars } from '../../../constants/modal-vars';
import { cookieVariables } from '../../../constants/cookie-variables.constants';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import { pageTypes } from '../../../constants/tracking.constants';
import { gaEventActions } from '../../../constants/ga-tracking.constants';
import { LIME_MAIN } from '../../constants/shared.constants';
import { NativeAppService } from '@shared/common/services/native-app/native-app.service';
import { RouterMap } from '../../../core/router-map';
import { navMenuItems } from './constants/header-menu.constants';
import { helpCenterEvents } from '../../../constants/help-center-events.constants';
import { isPlatformBrowser } from '@angular/common';

declare global {
  interface Window {
    zESettings: any;
  }
}

@Component({
  selector: 'app-header-menu',
  templateUrl: './header-menu.component.html',
  styleUrls: ['./header-menu.component.scss'],
  standalone: false
})
export class HeaderMenuComponent implements OnInit, OnDestroy {
  public langParam: languages | string;
  selectedLang: languages | string;
  languagesConfig: Array<{ code: languages | string; name: string }> = [];
  imgixHost = environment.imgixHost;
  navOverlaysState = {
    myAccount: false,
    langSwitcher: false,
    aboutUs: false,
    realEstate: false
  };
  isAuthModalOpen = false;
  environment = environment;
  externalLinks = externalLinks;
  viewPortWith = '';
  activeMenu = '';
  langSwitcherOpened: boolean;
  authModalType = authType;
  authModalActiveTab: authType;
  private readonly _destruction$: Subject<void> = new Subject<void>();
  profile: CustomerProfile;
  profile$: Observable<Customer>;
  menuIsOpen = false;
  modalVars = modalVars;
  howItWorksId = 'how-it-works';
  gaEventActions = gaEventActions;
  pageTypes = pageTypes;
  isHowItWorksTooltipVisible: boolean;
  currentPage; // key of external link page, needed for elements navigation
  guestHubUrl = LIME_MAIN.LIME_STAY_GUEST_HUB;
  isPDP: boolean;
  mainMenu = navMenuItems;
  isDropdownOpen = false;

  @Input() set menuIsOpenState(menuIsOpen: boolean) {
    this.menuIsOpen = menuIsOpen;
    let el = document.getElementsByClassName('main-menu__container')[0];

    if (!this.menuIsOpen && el) {
      el.scrollTo(0, 0);
    }
  }
  @Input() isCheckoutPage: boolean;
  @Input() langSwitcherIsOpen: boolean;
  @Input() isPostBookingPage: boolean;
  @Output() openLangSwitch = new EventEmitter<void>();
  @Output() closeLangSwitch = new EventEmitter<void>();
  @Output() toggleMenu = new EventEmitter<string>();
  @HostListener('document:click', ['$event'])
  @ViewChild('dropdownRef', { static: false })
  dropdownElement: ElementRef;

  click() {
    // on desktop, only one item can be active, so we're closing all of them first
    if (!this.isMobile) {
      this.navOverlaysState.myAccount = false;
      this.navOverlaysState.aboutUs = false;
      this.navOverlaysState.realEstate = false;
    }
  }

  get isElements(): boolean {
    return !!(window as any).__isAngularElement__;
  }

  get isMobile() {
    if (isPlatformBrowser(this.platformId)) {
      return this.viewportService.isMobileDevice();
    }
    return false;
  }

  get isDesktop() {
    return this.viewportService.isDesktopDevice();
  }

  get myAccountLink() {
    const isMobileWeb = this.isMobile && !this.nativeAppService.isNativeApp();
    return isMobileWeb ? RouterMap.Account.Mobile.url([]) : RouterMap.Account.url(['profile']);
  }

  constructor(
    private readonly translateService: TranslateService,
    private readonly viewportService: ViewportService,
    public authService: AuthService,
    public router: Router,
    public modalService: ModalService,
    public languageService: LanguageService,
    public locationService: LocationService,
    private readonly messageService: MessageService,
    private readonly resizeService: ResizeService,
    private readonly cookieAppService: CookieAppService,
    private readonly store: Store,
    private readonly activatedRoute: ActivatedRoute,
    private readonly nativeAppService: NativeAppService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Optional() @Inject(REQUEST) private readonly request: Request
  ) {
    this.profile$ = this.store.select((state) => state.customer?.customer);

    this.profile$.pipe(takeUntil(this._destruction$)).subscribe((customer: Customer) => {
      this.profile = customer?.profile;
    });

    this.messageService
      .getLanguageChange()
      .pipe(takeUntil(this._destruction$))
      .subscribe((message) => {
        const lang = message.text;
        if (lang) {
          this.updateCurrLang(lang);
        }
      });

    router.events
      .pipe(filter((ev: RouterEvent) => ev instanceof NavigationStart))
      .pipe(takeUntil(this._destruction$))
      .subscribe(() => {
        this.closeMenu();
        this.checkIsPDP();
      });

    let prevWidth = window.innerWidth;

    this.resizeService
      .resize()
      .pipe(takeUntil(this._destruction$))
      .subscribe(() => {
        if (window.innerWidth !== prevWidth) {
          prevWidth = window.innerWidth;
          this.navOverlaysState = {
            myAccount: false,
            langSwitcher: false,
            aboutUs: false,
            realEstate: false
          };
        }
      });

    this.languagesConfig = Object.keys(languages).map((code) => {
      return {
        code,
        name: languageDetails[code].name
      };
    });

    this.authService.openAuthModal.pipe(takeUntil(this._destruction$)).subscribe((type: authType) => {
      this.openAuthModal(null, type);
    });
  }

  ngOnInit() {
    // sets language param
    this.langParam = this.translateService.currentLang ?? languageDetails['en'].code;
    this.selectedLang = this.langParam;
    this.navigationEndSubscription();
    this.checkIsPDP();
  }

  get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  get isLoggedIn() {
    return this.isBrowser && this.authService.isLoggedInSubject.value;
  }

  get helpCenterLink() {
    const { headerHowItWorksMenu } = helpCenterEvents.sourceElement;
    return this.locationService.buildHelpCenterLink(headerHowItWorksMenu);
  }

  get helpAndFaqsTooltipText() {
    return this.translateService.instant('help-center.title-aria-label');
  }

  get isHelpPageActive() {
    return this.locationService.isHelpPageActive;
  }

  get showDivider() {
    return !this.isMobile && !this.isHelpPageActive;
  }

  ngOnDestroy() {
    this._destruction$.next();
    this._destruction$.complete();
  }

  navigationEndSubscription(): void {
    this.router.events
      .pipe(
        filter((navigation) => navigation instanceof NavigationEnd),
        takeUntil(this._destruction$)
      )
      .subscribe(() => {
        this.checkIsPDP();
      });
  }

  switchLanguage(event: Event, lang: languages | string) {
    event.preventDefault();

    if (lang) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { lang },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });

      this.updateCurrLang(lang);
    }
  }

  selectLanguage(event: Event, lang: languages | string) {
    event.preventDefault();
    this.selectedLang = lang;
  }

  updateCurrLang(lang: languages | string) {
    this.languageService.changeLang(lang);
    this.langParam = lang;
    this.selectedLang = this.langParam;

    if (this.navOverlaysState.langSwitcher) {
      this.toggleLangSwitcher(false);
      // Toggle menu only for mobile and portrait tablets
      this.onMenuItemClick();
    }

    return false;
  }

  onMenuItemClick() {
    this.closeMenu();
  }

  closeMenu() {
    if (this.isMobile) {
      this.toggleMenu.next('close');
    }
  }

  openAuthModal(event = null, authModalActiveTab?: authType) {
    this.isAuthModalOpen = true;
    this.modalService.open('auth');
    event?.preventDefault();
    this.onMenuItemClick();
    this.authModalActiveTab = authModalActiveTab;
  }

  closeLangSwitcher() {
    if (this.navOverlaysState.langSwitcher && this.langSwitcherOpened) {
      this.closeLangSwitch.next();
      this.navOverlaysState.langSwitcher = false;
    }
    this.langSwitcherOpened = true;
    return false;
  }

  toggleLangSwitcher(isOpen?: boolean) {
    if (!isOpen) {
      isOpen = !this.navOverlaysState.langSwitcher;
    }

    if (isOpen) {
      this.openLangSwitch.next();
    } else {
      this.closeLangSwitch.next();
    }
    this.langSwitcherOpened = false;
    this.selectedLang = '';
    this.navOverlaysState.langSwitcher = isOpen;
    return false;
  }

  logOut(event) {
    this.onMenuItemClick();
    this.authService.logOut();
    event.preventDefault();
  }

  checkIsPDP() {
    this.isPDP = this.router.url.includes('suites');
  }

  hideHowItWorksTooltip() {
    if (!this.isMobile && this.isHowItWorksTooltipVisible) {
      this.isHowItWorksTooltipVisible = false;
    }
  }

  resetAuthModalActiveTab() {
    this.authModalActiveTab = null;
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      //  focus first dropdown item
      setTimeout(() => {
        const firstItem = document.querySelector('.dropdown-menu [role="menuitem"]');
        if (firstItem) {
          (firstItem as HTMLElement).focus();
        }
      }, 0);
    }
  }

  handleKeydown(event: KeyboardEvent) {
    const isMenuItem = event.target instanceof HTMLAnchorElement;

    if (event.key === 'Enter' || event.key === ' ') {
      if (!isMenuItem) {
        event.preventDefault();
        this.toggleDropdown();
      }
    }

    if (event.key === 'Escape') {
      this.isDropdownOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.dropdownElement && !this.dropdownElement.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  @HostListener('window:focus', ['$event'])
  onFocus(event: any): void {
    const cookieLang = this.cookieAppService.getCookie(cookieVariables.lang) as languages;
    if (!!cookieLang && !this.isElements) {
      this.updateCurrLang(cookieLang);
    }
  }
}
